import { useMutation } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { useApi } from "@/api/hooks/useApi";
import type { QuestionRequest } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { Form } from "@/components/Form/Form";
import { FormField } from "@/components/Form/FormField";
import { FormInput } from "@/components/Form/FormInput";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { LazyMarkdownRenderer } from "@/components/MarkdownRenderer/LazyMarkdownRenderer";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { AskAiModalSubscriptionFooter } from "./AskAiModalSubscriptionFooter";

type AskAiModalProps = ModalBaseProps;

interface AskAiModalFormValues {
  question: string;
}

export function AskAiModal({ isOpened, onOpenChange }: AskAiModalProps): React.ReactNode {
  const [currResponse, setCurrResponse] = useState<string | undefined>(undefined);
  const [isFeatureAvailable, featureAvailabilityHandlers] = useBool(true);

  const { t } = useTranslation();
  const formMethods = useForm<AskAiModalFormValues>({
    defaultValues: {
      question: "",
    },
  });
  const api = useApi();

  const question = useWatch<AskAiModalFormValues, "question">({
    name: "question",
    control: formMethods.control,
  });

  const {
    mutate: postQuestion,
    isPending: isPendingPostQuestion,
    isError: isErrorPostQuestion,
    variables: questionVariables,
  } = useMutation({
    mutationFn: (data: QuestionRequest) => api.postProjectSummaryQuestionV1(data),
    onSuccess: (response) => {
      if (response.data === null) {
        featureAvailabilityHandlers.setFalse();
      } else {
        setCurrResponse(response.data);
      }
    },
  });

  useEffect(() => {
    if (!isOpened) {
      formMethods.reset();
      setCurrResponse(undefined);
    }
  }, [formMethods, isOpened]);

  const onSubmit = (data: AskAiModalFormValues) => {
    postQuestion({ ...data });
  };

  // Whether the question that was asked is the same as the one that's currently in the input field
  const isInputQuestionForResponse = questionVariables?.question === question;

  return (
    <Modal.Root
      title={t("component.ask-ai-modal.title")}
      description={t("component.ask-ai-modal.subtitle")}
      size={currResponse ? "lg" : "md"}
      {...{ isOpened, onOpenChange }}
    >
      <Form className="relative flex flex-col items-center gap-4" {...{ formMethods, onSubmit }}>
        <div className="flex w-full items-center justify-center gap-2">
          <FormField label={t("component.ask-ai-modal.question-input.label")} htmlFor="question" hideLabel>
            <FormInput
              className="w-full"
              id="question"
              name="question"
              placeholder={t("component.ask-ai-modal.question-input.placeholder")}
              rules={{
                required: t("components.form.error.required", {
                  inputName: t("component.ask-ai-modal.question-input.label"),
                }),
              }}
            />
          </FormField>
          <Button type="submit" disabled={isPendingPostQuestion}>
            {t("component.ask-ai-modal.ask.btn")}
          </Button>
        </div>
        <div className="flex min-h-20 w-full flex-1 flex-col items-center justify-center gap-4">
          {isPendingPostQuestion && <FullSizeLoader />}
          {!isPendingPostQuestion && !isErrorPostQuestion && currResponse && (
            <div className="flex w-full flex-col gap-2">
              <LazyMarkdownRenderer markdown={currResponse} />
            </div>
          )}
          {!isPendingPostQuestion && isFeatureAvailable && !currResponse && (
            <div className="flex w-full flex-col gap-1">
              <h4 className="text-headline4">{t("component.ask-ai-modal.popular-questions.title")}</h4>
              <ul className="ml-3 list-disc pl-4">
                <PopularQuestion
                  question={t("component.ask-ai-modal.popular-questions.item-1")}
                  onClick={(q) => {
                    formMethods.setValue("question", q);
                    postQuestion({ question: q });
                  }}
                />
                <PopularQuestion
                  question={t("component.ask-ai-modal.popular-questions.item-2")}
                  onClick={(q) => {
                    formMethods.setValue("question", q);
                    postQuestion({ question: q });
                  }}
                />
                <PopularQuestion
                  question={t("component.ask-ai-modal.popular-questions.item-3")}
                  onClick={(q) => {
                    formMethods.setValue("question", q);
                    postQuestion({ question: q });
                  }}
                />
              </ul>
            </div>
          )}
          {!isPendingPostQuestion && !isFeatureAvailable && (
            <p className="text-red-600">{t("component.ask-ai-modal.no-data.notification")}</p>
          )}
          {!isPendingPostQuestion && !isErrorPostQuestion && (
            <AskAiModalSubscriptionFooter
              {...{ questionVariables, isInputQuestionForResponse }}
              hasResponse={!!currResponse}
            />
          )}
        </div>
      </Form>
    </Modal.Root>
  );
}

function PopularQuestion({ question, onClick }: { question: string; onClick: (question: string) => void }) {
  return (
    <li>
      <Button className="whitespace-pre-wrap text-left" styling="ghostPrimary" onClick={() => onClick(question)}>
        {question}
      </Button>
    </li>
  );
}
